import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Code, 
  Database, 
  Cloud, 
  Cpu, 
  Gauge, 
  Monitor,
  ArrowRight,
  CheckCircle,
  CheckCircle2
} from 'lucide-react'
import { services } from '../lib/data'

const DevelopmentPage = () => {
  const developmentServices = services.filter(s => s.category === 'development')

  const techStack = {
    frontend: ['React', 'TypeScript', 'Next.js', 'Electron', 'Tailwind CSS'],
    backend: ['Node.js', 'Java', 'Python', 'Go', 'PostgreSQL', 'MongoDB'],
    cloud: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins'],
    monitoring: ['Grafana', 'Prometheus', 'ELK Stack', 'New Relic']
  }

  const architecturePatterns = [
    {
      name: 'Microservices',
      description: 'Scalable, maintainable service-oriented architecture',
      benefits: ['Independent deployment', 'Technology diversity', 'Fault isolation', 'Scalability']
    },
    {
      name: 'Event-Driven',
      description: 'Loose coupling through asynchronous communication',
      benefits: ['Decoupled services', 'Real-time processing', 'Scalability', 'Reliability']
    },
    {
      name: 'CQRS',
      description: 'Command Query Responsibility Segregation pattern',
      benefits: ['Performance optimization', 'Scalability', 'Flexibility', 'Maintainability']
    }
  ]

  return (
    <>
      <Helmet>
        <title>Development Services - DevSecRoot</title>
        <meta name="description" content="Full-stack development services including microservices architecture, cloud integration, CI/CD pipelines, and modern web applications." />
        <link rel="canonical" href="https://devsecroot.com/development" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Development Services
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Build secure, scalable, and high-performance applications with modern technologies 
              and proven architectural patterns. From concept to deployment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Our Development Services
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              End-to-end development solutions designed to build robust, scalable, 
              and secure applications that drive business growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {developmentServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6 h-full flex flex-col"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Code className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-xl font-semibold text-dsr-text mb-3">{service.title}</h3>
                <p className="text-dsr-text/70 mb-4 flex-grow">{service.description}</p>
                
                <div className="space-y-2 mb-6">
                  {service.features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-dsr-accent flex-shrink-0" />
                      <span className="text-sm text-dsr-text/80">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to={service.link}
                  className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors mt-auto"
                >
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Technology Stack
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Modern, proven technologies that enable us to build robust and scalable solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(techStack).map(([category, technologies], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  {category === 'frontend' && <Monitor className="w-6 h-6 text-black" />}
                  {category === 'backend' && <Database className="w-6 h-6 text-black" />}
                  {category === 'cloud' && <Cloud className="w-6 h-6 text-black" />}
                  {category === 'monitoring' && <Gauge className="w-6 h-6 text-black" />}
                </div>
                
                <h3 className="text-lg font-semibold text-dsr-text mb-4 capitalize">
                  {category}
                </h3>
                
                <div className="space-y-2">
                  {technologies.map((tech, idx) => (
                    <div key={idx} className="text-sm text-dsr-accent bg-dsr-accent/10 px-3 py-1 rounded-lg">
                      {tech}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Patterns */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Architecture Patterns
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Proven architectural patterns that ensure scalability, maintainability, and performance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {architecturePatterns.map((pattern, index) => (
              <motion.div
                key={pattern.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Cpu className="w-8 h-8 text-black" />
                </div>
                
                <h3 className="text-xl font-semibold text-dsr-text mb-3 text-center">{pattern.name}</h3>
                <p className="text-dsr-text/70 mb-6 text-center">{pattern.description}</p>
                
                <div className="space-y-2">
                  {pattern.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-dsr-accent flex-shrink-0" />
                      <span className="text-sm text-dsr-text/80">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
                Our Development Process
              </h2>
              <p className="text-lg text-dsr-text/70 mb-6">
                We follow an agile, iterative approach that ensures quality, security, 
                and timely delivery. Our process is designed to adapt to your needs 
                and provide continuous value.
              </p>
              <p className="text-lg text-dsr-text/70 mb-6">
                From initial requirements gathering to deployment and maintenance, 
                we maintain clear communication and deliver working software at every iteration.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              {[
                { step: '1', title: 'Discovery & Planning', description: 'Requirements analysis and architecture design' },
                { step: '2', title: 'Development & Testing', description: 'Agile development with continuous testing' },
                { step: '3', title: 'Security Integration', description: 'Built-in security at every layer' },
                { step: '4', title: 'Deployment & Monitoring', description: 'CI/CD pipeline with ongoing monitoring' }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card-hover p-4"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-black">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-dsr-text">{item.title}</h4>
                      <p className="text-sm text-dsr-text/70">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Ready to Build Something Amazing?
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Let's discuss your project requirements and create a secure, scalable solution together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Start Your Project
              </Link>
              <Link to="/case-studies" className="btn-secondary text-lg px-8 py-4">
                View Development Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default DevelopmentPage
